import React from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { withAlpha } from 'utils'
import LocaleText from 'components/LocaleText'

export type ChipVariant = 'filled' | 'outline' | 'ghost'
export type ChipSize = 'sm' | 'md'

export interface IChipProps {
  /** Strings use the locale catalogue; nodes preserve caller-resolved text and rich labels. */
  label: React.ReactNode
  color?: string
  variant?: ChipVariant
  dot?: boolean
  prefix?: React.ReactNode
  icon?: React.ReactNode
  onClick?: () => void
  size?: ChipSize
  radius?: string
  [key: string]: any
}

const SIZES: Record<ChipSize, { px: string; py: string; fs: string; dotSize: string }> = {
  sm: { px: '7px', py: '2px', fs: '10px', dotSize: '5px' },
  md: { px: '10px', py: '4px', fs: '12px', dotSize: '6px' },
}

export const getChipVariantStyles = (variant: ChipVariant, color: string, interactive: boolean) => {
  const hoverActive = interactive
    ? {
        _active: { transform: 'scale(0.97)' },
        _focus: { boxShadow: `0 0 0 3px ${withAlpha(color, 0.18)}`, outline: 'none' },
      }
    : {}

  switch (variant) {
    case 'filled':
      return {
        bg: withAlpha(color, 0.1),
        border: `1px solid ${withAlpha(color, 0.18)}`,
        color,
        ...(interactive ? { _hover: { bg: withAlpha(color, 0.16) } } : {}),
        ...hoverActive,
        _disabled: { opacity: 0.45, cursor: 'not-allowed' },
      }

    case 'outline':
      return {
        bg: 'transparent',
        border: `1px solid ${withAlpha(color, 0.45)}`,
        color,
        ...(interactive ? { _hover: { bg: withAlpha(color, 0.06) } } : {}),
        ...hoverActive,
        _disabled: { opacity: 0.45, cursor: 'not-allowed' },
      }

    case 'ghost':
      return {
        bg: '#FFFFFF',
        border: '1px solid #E2E8F0',
        color: '#475569',
        ...(interactive ? { _hover: { bg: '#F8FAFC', borderColor: '#CBD5E1' } } : {}),
        ...(interactive
          ? {
              _active: { bg: '#F1F5F9', transform: 'scale(0.97)' },
              _focus: { boxShadow: '0 0 0 3px rgba(15,23,42,0.08)', outline: 'none' },
            }
          : {}),
        _disabled: { opacity: 0.45, cursor: 'not-allowed' },
      }
  }
}

export const Chip = React.forwardRef<HTMLElement, IChipProps>(
  (
    {
      label,
      color = '#64748B',
      variant = 'filled',
      dot = false,
      prefix,
      icon,
      onClick,
      size = 'md',
      radius = '999px',
      ...rest
    },
    ref
  ) => {
    const sz = SIZES[size]
    const interactive = !!onClick
    const variantStyles = getChipVariantStyles(variant, color, interactive)
    const textColor = variant === 'ghost' ? '#475569' : color
    const showDot = dot && prefix === undefined
    const showPrefix = prefix !== undefined

    return (
      <Flex
        ref={ref as any}
        as={interactive ? 'button' : 'span'}
        {...(interactive ? { type: 'button' as const } : {})}
        display="inline-flex"
        align="center"
        flexShrink={0}
        borderRadius={radius}
        px={sz.px}
        py={sz.py}
        cursor={interactive ? 'pointer' : 'default'}
        transition="all 0.15s ease-out"
        style={{ gap: '5px', outline: 'none' }}
        onClick={onClick}
        {...variantStyles}
        {...rest}
      >
        {showDot && <Box w={sz.dotSize} h={sz.dotSize} borderRadius="50%" bg={color} flexShrink={0} />}
        {showPrefix && (
          <span style={{ fontSize: sz.fs, fontWeight: 700, color: textColor, lineHeight: '1', flexShrink: 0 }}>
            {prefix}
          </span>
        )}
        {icon && (
          <Box display="inline-flex" alignItems="center" flexShrink={0} color={textColor}>
            {icon}
          </Box>
        )}
        <span style={{ fontSize: sz.fs, fontWeight: 600, color: textColor, lineHeight: '1.3', whiteSpace: 'nowrap' }}>
          {typeof label === 'string' ? <LocaleText text={label} /> : label}
        </span>
      </Flex>
    )
  }
)

Chip.displayName = 'Chip'
